import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { shiftAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Clock, Moon, Sun } from 'lucide-react';

const ShiftManagement = () => {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newShift, setNewShift] = useState({
    name: '',
    code: '',
    shift_type: 'fixed',
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: 60,
    grace_period_minutes: 15,
    late_threshold_minutes: 30,
    overtime_multiplier: 1.25
  });

  const fetchShifts = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const response = await shiftAPI.getAll(user.company_id);
      setShifts(response.data);
    } catch (err) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handleAddShift = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newShift,
        break_minutes: parseInt(newShift.break_minutes),
        grace_period_minutes: parseInt(newShift.grace_period_minutes),
        late_threshold_minutes: parseInt(newShift.late_threshold_minutes),
        overtime_multiplier: parseFloat(newShift.overtime_multiplier)
      };
      await shiftAPI.create(user.company_id, data);
      toast.success('Shift template created');
      setIsAddOpen(false);
      setNewShift({
        name: '',
        code: '',
        shift_type: 'fixed',
        start_time: '08:00',
        end_time: '17:00',
        break_minutes: 60,
        grace_period_minutes: 15,
        late_threshold_minutes: 30,
        overtime_multiplier: 1.25
      });
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shift');
    }
  };

  const isOvernight = (start, end) => end < start;

  if (loading) {
    return (
      <DashboardLayout title="Shift Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Shift Management" 
      subtitle="Configure shift templates for your workforce"
    >
      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-shift-btn">
              <Plus className="h-4 w-4" />
              Add Shift Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Shift Template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddShift} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Shift Name</Label>
                  <Input
                    value={newShift.name}
                    onChange={(e) => setNewShift({...newShift, name: e.target.value})}
                    placeholder="e.g. Day Shift"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    value={newShift.code}
                    onChange={(e) => setNewShift({...newShift, code: e.target.value})}
                    placeholder="e.g. DAY"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Shift Type</Label>
                <Select
                  value={newShift.shift_type}
                  onValueChange={(value) => setNewShift({...newShift, shift_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="rotating">Rotating</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newShift.start_time}
                    onChange={(e) => setNewShift({...newShift, start_time: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={newShift.end_time}
                    onChange={(e) => setNewShift({...newShift, end_time: e.target.value})}
                    required
                  />
                </div>
              </div>
              {isOvernight(newShift.start_time, newShift.end_time) && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                  <Moon className="h-4 w-4" />
                  This is an overnight shift (crosses midnight)
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Break (minutes)</Label>
                  <Input
                    type="number"
                    value={newShift.break_minutes}
                    onChange={(e) => setNewShift({...newShift, break_minutes: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Grace Period (mins)</Label>
                  <Input
                    type="number"
                    value={newShift.grace_period_minutes}
                    onChange={(e) => setNewShift({...newShift, grace_period_minutes: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Late Threshold (mins)</Label>
                  <Input
                    type="number"
                    value={newShift.late_threshold_minutes}
                    onChange={(e) => setNewShift({...newShift, late_threshold_minutes: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>OT Multiplier</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newShift.overtime_multiplier}
                    onChange={(e) => setNewShift({...newShift, overtime_multiplier: e.target.value})}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full">Create Shift</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shifts Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Shift</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Break</TableHead>
                <TableHead>Grace</TableHead>
                <TableHead>OT Rate</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shifts.map((shift) => (
                <TableRow key={shift.id} data-testid={`shift-row-${shift.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {shift.is_overnight ? (
                        <Moon className="h-4 w-4 text-purple-500" />
                      ) : (
                        <Sun className="h-4 w-4 text-amber-500" />
                      )}
                      <span className="font-medium">{shift.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{shift.code}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{shift.shift_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      {shift.start_time} - {shift.end_time}
                    </div>
                  </TableCell>
                  <TableCell>{shift.break_minutes} mins</TableCell>
                  <TableCell>{shift.grace_period_minutes} mins</TableCell>
                  <TableCell>{shift.overtime_multiplier}x</TableCell>
                  <TableCell>
                    <Badge className={shift.is_active ? 'status-approved' : 'status-rejected'}>
                      {shift.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {shifts.length === 0 && (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No shift templates yet</p>
              <p className="text-sm text-muted-foreground">Create your first shift template to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default ShiftManagement;
